<script setup lang="ts">
/**
 * Zone de signature tactile (canvas) — module Onboarding / Offboarding V2.
 *
 * Dessin via Pointer Events (souris + tactile + stylet), backing store mis à
 * l'échelle du devicePixelRatio pour un trait net sur écrans HiDPI. À chaque fin
 * de trait, émet une data URL PNG (`update:modelValue`) → compatible `v-model`.
 *
 * Le trait reprend la couleur d'encre de la marque via `currentColor` : on pose
 * `text-primary` sur le wrapper et on relit la couleur calculée au montage (pas
 * de hex en dur — cf. Cardinal rules MASTER §1).
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { TrashIcon } from '@heroicons/vue/24/outline'

const props = withDefaults(
  defineProps<{
    modelValue: string | null
    label: string
    disabled?: boolean
  }>(),
  { disabled: false },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
}>()

/** Élément racine : porte `text-primary` → sert de source à `currentColor`. */
const root = ref<HTMLDivElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)

let ctx: CanvasRenderingContext2D | null = null
let drawing = false
/** Couleur du trait, résolue depuis `currentColor` au montage. */
let strokeColor = '#082a36'
let resizeObserver: ResizeObserver | null = null

/** (Re)dimensionne le backing store selon le DPR et réapplique le style du trait. */
function setupCanvas(): void {
  const el = canvas.value
  if (!el) return
  const dpr = window.devicePixelRatio || 1
  const cssWidth = el.clientWidth
  const cssHeight = el.clientHeight
  if (cssWidth === 0 || cssHeight === 0) return

  // Sauvegarde du contenu courant pour le restaurer après redimensionnement.
  const previous = props.modelValue

  el.width = Math.round(cssWidth * dpr)
  el.height = Math.round(cssHeight * dpr)

  ctx = el.getContext('2d')
  if (!ctx) return
  ctx.scale(dpr, dpr)
  applyStrokeStyle()

  if (previous) drawDataUrl(previous)
}

/** Applique l'épaisseur, les jointures et la couleur d'encre au contexte. */
function applyStrokeStyle(): void {
  if (!ctx) return
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = strokeColor
}

/** Coordonnées du pointeur relatives au canvas (en pixels CSS). */
function pointerPos(event: PointerEvent): { x: number; y: number } {
  const el = canvas.value!
  const rect = el.getBoundingClientRect()
  return { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

function onPointerDown(event: PointerEvent): void {
  if (props.disabled || !ctx || !canvas.value) return
  drawing = true
  canvas.value.setPointerCapture(event.pointerId)
  const { x, y } = pointerPos(event)
  ctx.beginPath()
  ctx.moveTo(x, y)
}

function onPointerMove(event: PointerEvent): void {
  if (!drawing || !ctx) return
  const { x, y } = pointerPos(event)
  ctx.lineTo(x, y)
  ctx.stroke()
}

function endStroke(event: PointerEvent): void {
  if (!drawing) return
  drawing = false
  canvas.value?.releasePointerCapture?.(event.pointerId)
  emitDataUrl()
}

/** Émet la signature courante (data URL PNG). */
function emitDataUrl(): void {
  if (!canvas.value) return
  emit('update:modelValue', canvas.value.toDataURL('image/png'))
}

/** Efface le canvas et émet `null`. */
function clear(): void {
  if (!ctx || !canvas.value) return
  const dpr = window.devicePixelRatio || 1
  ctx.clearRect(0, 0, canvas.value.width / dpr, canvas.value.height / dpr)
  emit('update:modelValue', null)
}

/** Dessine une data URL existante sur le canvas (restauration / valeur initiale). */
function drawDataUrl(dataUrl: string): void {
  if (!ctx || !canvas.value) return
  const dpr = window.devicePixelRatio || 1
  const img = new Image()
  img.onload = () => {
    ctx!.drawImage(img, 0, 0, canvas.value!.width / dpr, canvas.value!.height / dpr)
  }
  img.src = dataUrl
}

onMounted(() => {
  // Résout la couleur d'encre depuis `currentColor` (token primary du wrapper).
  if (root.value) {
    const resolved = getComputedStyle(root.value).color
    if (resolved) strokeColor = resolved
  }
  setupCanvas()

  // Le canvas suit la largeur du conteneur (responsive, rotation mobile…).
  if (canvas.value && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => setupCanvas())
    resizeObserver.observe(canvas.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

// Si le parent réinitialise la valeur à null (ex. reset du formulaire), nettoyer.
watch(
  () => props.modelValue,
  (value) => {
    if (value === null && ctx && canvas.value && !drawing) {
      const dpr = window.devicePixelRatio || 1
      ctx.clearRect(0, 0, canvas.value.width / dpr, canvas.value.height / dpr)
    }
  },
)
</script>

<template>
  <div ref="root" class="flex flex-col gap-2 text-primary">
    <div class="relative rounded-lg border border-base-300 bg-base-100">
      <canvas
        ref="canvas"
        class="block h-40 w-full rounded-lg [touch-action:none]"
        :class="{ 'cursor-not-allowed opacity-60': disabled }"
        role="img"
        :aria-label="`Zone de signature — ${label}. Signez dans le cadre.`"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="endStroke"
        @pointerleave="endStroke"
      ></canvas>

      <button
        type="button"
        class="btn btn-ghost btn-sm absolute right-1 top-1 motion-reduce:transition-none"
        :disabled="disabled || !modelValue"
        aria-label="Effacer la signature"
        @click="clear"
      >
        <TrashIcon class="h-5 w-5" aria-hidden="true" />
        Effacer
      </button>
    </div>

    <div class="flex flex-col gap-0.5 text-base-content">
      <span class="border-t border-base-300 pt-1 text-base font-medium">{{ label }}</span>
      <span class="text-sm text-base-content/80">Signez dans le cadre ci-dessus.</span>
    </div>
  </div>
</template>
